import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import ErrorKey from '@/util/ErrorKey';
import { StoreResponse, StoreResponses } from '@/stores/response';
import {
  DynPageDto,
  FooterFragmentDto,
  FormSubmissionFieldDto,
  HeaderFragmentDto,
  RegisterFragmentDto,
  OrderFragmentDto,
} from '@/api';
import { cmsApi, cmsFormSubmissionApi } from '@/stores/api';
import { ShopContext } from '@/util/ShopContext';
import moment from 'moment';

@Module({
  namespaced: true,
  name: 'CmsStore',
})
export class CmsStore extends VuexModule {
  // Note: This store is cached using vuex-persist. See vuex-persist-config.ts!
  private cacheableHeader: CachedFragment<HeaderFragmentDto | undefined> | null = null;
  private cacheableFooter: CachedFragment<FooterFragmentDto | undefined> | null = null;

  public get header(): HeaderFragmentDto | null {
    return this.cacheableHeader?.value ?? null;
  }

  public get footer(): FooterFragmentDto | null {
    return this.cacheableFooter?.value ?? null;
  }

  @Action
  public async fetchDynamicPage(
    request: { webrootPath: string, language: string },
  ): Promise<StoreResponse<DynPageDto>> {
    const {webrootPath, language} = request;
    try {
      const response = await cmsApi.getCmsDynPage(ShopContext.tenantCode, ShopContext.shopCode, language, webrootPath);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.CmsFetchAction);
    }
  }

  @Action
  public async fetchRegisterFragment(language: string): Promise<StoreResponse<RegisterFragmentDto>> {
    try {
      const response = await cmsApi.getRegisterFragment(ShopContext.tenantCode, ShopContext.shopCode, language);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.CmsFetchAction);
    }
  }

  private static isApplicable(cachedFragment: CachedFragment<any> | null, language: string): boolean {
    return cachedFragment !== null &&
      cachedFragment.language === language &&
      cachedFragment.shopCode === ShopContext.shopCode &&
      cachedFragment.tenantCode === ShopContext.tenantCode &&
      moment(cachedFragment.expiryIso).isAfter(moment());
  }

  private static wrapCacheable<T>(value: T, language: string): CachedFragment<T> {
    return {
      value, language,
      expiryIso: moment().add(30, 'minutes').toISOString(false),
      tenantCode: ShopContext.tenantCode,
      shopCode: ShopContext.shopCode,
    };
  }

  @Action
  public async fetchHeaderIfNecessary(language: string): Promise<StoreResponse<HeaderFragmentDto>> {
    if (CmsStore.isApplicable(this.cacheableHeader, language)) {
      return StoreResponses.success(this.cacheableHeader?.value!!);
    }
    try {
      const response = await cmsApi.getHeaderFragment(ShopContext.tenantCode, ShopContext.shopCode, language);
      this.context.commit('setHeader', {header: response.data, language});
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.suppressedError(e, ErrorKey.CmsFetchAction);
    }
  }

  @Action
  public async fetchFooterIfNecessary(language: string): Promise<StoreResponse<FooterFragmentDto>> {
    if (CmsStore.isApplicable(this.cacheableFooter, language)) {
      return StoreResponses.success(this.cacheableFooter?.value!!);
    }
    try {
      const response = await cmsApi.getFooterFragment(ShopContext.tenantCode, ShopContext.shopCode, language);
      this.context.commit('setFooter', {footer: response.data, language});
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.suppressedError(e, ErrorKey.CmsFetchAction);
    }
  }

  @Action
  public async fetchOrderFragment(language: string): Promise<StoreResponse<OrderFragmentDto>> {
    try {
      const response = await cmsApi.getOrderFragment(ShopContext.tenantCode, ShopContext.shopCode, language);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.CmsFetchAction);
    }
  }

  @Action
  public async submitCmsForm(
    request: { webrootPath: string, language: string, formFields: FormSubmissionFieldDto[], captchaToken: string },
  ): Promise<StoreResponse<any>> {
    const {webrootPath, language, formFields, captchaToken} = request;
    try {
      const response = await cmsFormSubmissionApi.submitFormData(
        ShopContext.tenantCode,
        ShopContext.shopCode,
        language,
        webrootPath,
        formFields,
        {
          params: {
            'g-recaptcha-response': captchaToken,
          },
        },
      );
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.CmsFormSubmissionException);
    }
  }

  @Mutation
  private setHeader(payload: { header?: HeaderFragmentDto, language: string }): void {
    this.cacheableHeader = CmsStore.wrapCacheable(payload.header, payload.language);
  }

  @Mutation
  private setFooter(payload: { footer?: FooterFragmentDto, language: string }): void {
    this.cacheableFooter = CmsStore.wrapCacheable(payload.footer, payload.language);
  }
}

interface CachedFragment<T> {
  value?: T;
  expiryIso: string;
  language: string;
  shopCode: string;
  tenantCode: string;
}
